/**
 * LienDetailPage
 * Detalle drill-down de un WalletLien. Muestra LienSummaryCard con historial
 * completo + datos del directory.
 *
 * Backend: GET /credit-worker/backoffice/liens/{lienId}
 */

import { useParams, Link } from 'react-router-dom';
import { RefreshCw, ArrowLeft } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { ROUTES } from '@/constants';
import { useLien } from '@/features/collection/hooks';
import { LienSummaryCard } from '@/features/collection/components';

const BREADCRUMBS = (lienId: string) => [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Cobranza', path: ROUTES.COLLECTION_DASHBOARD },
  { label: 'Wallet Liens', path: ROUTES.LIENS_LIST },
  { label: lienId.slice(0, 8) + '…', path: ROUTES.LIENS_LIST },
];

export function LienDetailPage() {
  const { lienId } = useParams<{ lienId: string }>();
  const { lien, collections, isLoading, isError, refetch } = useLien(lienId);

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Detalle de Lien" breadcrumbs={BREADCRUMBS(lienId || '')} />
        <div className="space-y-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  if (isError || !lien) {
    return (
      <div>
        <PageHeader title="Detalle de Lien" breadcrumbs={BREADCRUMBS(lienId || '')} />
        <Card>
          <p className="text-sm">No se pudo cargar el lien.</p>
          <Button onClick={refetch} className="mt-2">
            <RefreshCw size={16} /> Reintentar
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <Link
        to={ROUTES.LIENS_LIST}
        className="mb-2 inline-flex items-center gap-1 text-sm text-primary hover:underline"
      >
        <ArrowLeft size={14} /> Volver al listado
      </Link>
      <PageHeader
        title="Detalle de Lien"
        subtitle={`Lien ${lien.lien_id} — credit ${lien.credit_id}`}
        breadcrumbs={BREADCRUMBS(lien.lien_id)}
        actions={
          <Button onClick={refetch}>
            <RefreshCw size={16} /> Actualizar
          </Button>
        }
      />

      <Card title="IDs">
        <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
          <Field label="Lien ID" value={lien.lien_id} mono />
          <Field label="Credit ID" value={lien.credit_id} mono />
          <Field label="Directory ID" value={lien.directory_id} mono />
          <Field label="Partner" value={lien.partner_id} />
        </dl>
      </Card>

      <div className="mt-4">
        <LienSummaryCard lien={lien} collections={collections} />
      </div>
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs uppercase text-muted-foreground">{label}</dt>
      <dd className={mono ? 'font-mono text-xs' : ''}>{value}</dd>
    </div>
  );
}
